// AI pilot for offline versus (VS AI). Drives the RivalMech exactly like network snapshots
// would, but from a local simulation:
//   • a real Arcade body with the mech's movement (run, jump, double jump, dash + i-frames)
//     colliding with the arena (one-way platforms can be dropped through);
//   • a small decision loop: keep the weapon's preferred range, strafe, jump to reach the
//     player, dodge with dashes, go for drops (health when hurt, weapons when close);
//   • its real weapon type: auto / spread / charge / missile burst / lobber projectiles as
//     enemy shots (hit the player through the normal damage pipeline), arc lance as a beam;
//   • hp + shield with regen; hits from the player arrive via RivalMech → takeHit().
// Difficulty (0 easy · 1 normal · 2 hard) scales aim error, reaction, damage and dashing.

import Phaser from 'phaser';
import { PLAYER_BODY } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { GameContext } from '../../core/contracts';
import type { MechId, Tag, WeaponId } from '../../core/types';
import { art } from '../../art/registry';
import { WEAPONS } from '../../data/items';
import { MECHS } from '../../data/mechs';
import type { HitMsg, StateMsg } from '../../net/versus';
import { deriveTuning, jumpPhase, type MoveTuning } from '../player/movement';
import { BotKit, type KitPilot } from './botkit';
import type { RivalMech } from './rival';

interface Level {
  aimError: number;
  reaction: number;
  damage: number;
  /** Dashes per second (chance) when threatened. */
  dash: number;
  /** Share of time spent firing (burst duty cycle). */
  duty: number;
}

const LEVELS: Level[] = [
  { aimError: 0.17, reaction: 0.65, damage: 0.45, dash: 0.08, duty: 0.45 },
  { aimError: 0.085, reaction: 0.35, damage: 0.7, dash: 0.22, duty: 0.6 },
  { aimError: 0.035, reaction: 0.16, damage: 0.9, dash: 0.45, duty: 0.75 },
];

/** Shield starts regenerating this long after a hit (s), at this rate (/s). */
const SHIELD_DELAY = 2.6;
const SHIELD_REGEN = 14;
/** Drops the bot goes for (px). */
const DROP_INTEREST = 520;
/** Highest a drop may sit above the feet to be worth chasing (≈ a double jump), and how long to try (s). */
const DROP_REACH = 230;
const DROP_PATIENCE = 3;

export interface BotDrop {
  id: number;
  kind: 'weapon' | 'health' | 'item';
  x: number;
  y: number;
  landed: boolean;
}

export class BotPilot {
  private readonly zone: Phaser.GameObjects.Zone;
  private readonly body: Phaser.Physics.Arcade.Body;
  private readonly t: MoveTuning;
  private readonly lv: Level;
  hp: number;
  readonly maxHp: number;
  shield: number;
  readonly maxShield: number;
  weapon: WeaponId;
  private alive = true;
  private facing: 1 | -1;
  private aim = 0;
  private sinceHit = 99;
  private airJumps = 1;
  private sinceTakeoff = 9;
  private wasGround = true;
  private moveDir = 0;
  private strafeT = 0;
  /** Bastion: seconds left planted (standing still so the brace shield comes up). */
  private plantT = 0;
  private plantCd = 1.5;
  private hopT = 1;
  private dropT = 0;
  private dashT = 0;
  private dashCd = 1.5;
  private dashDir: 1 | -1 = 1;
  private seeT = 0;
  /** Drop being chased, for how long, and drops it gave up on. */
  private chaseId = -1;
  private chaseT = 0;
  private readonly gaveUp = new Set<number>();
  /** Time without line of sight to the player (s). */
  private blindT = 0;
  private fireT = 0;
  private dutyT = 0;
  private firing = false;
  private burstLeft = 0;
  private animT = 0;
  private reportedDead = false;
  /** Signature / ultimate / brace (see botkit.ts). */
  private readonly kit: BotKit;
  /** Dash speed multiplier of the current dash (Arc Dash lunges are faster). */
  private dashSpeedMult = 1;
  private lastDist = 9999;
  private lastLos = false;

  constructor(
    private readonly ctx: GameContext,
    private readonly rival: RivalMech,
    readonly mechId: MechId,
    x: number,
    feetY: number,
    level: number,
    private readonly report: (m: { t: string; [k: string]: unknown }) => void,
    private readonly round: number,
  ) {
    const def = MECHS[mechId] ?? MECHS.volt;
    this.t = deriveTuning(def.movement, def.base);
    this.lv = LEVELS[Math.max(0, Math.min(2, level))];
    this.hp = this.maxHp = def.base.maxHp;
    this.shield = this.maxShield = def.base.maxShield;
    this.weapon = def.startingWeapon;
    this.facing = x > ctx.world.width / 2 ? -1 : 1;
    const scene = ctx.scene;
    this.zone = scene.add.zone(x, feetY - PLAYER_BODY.h / 2 - 2, PLAYER_BODY.w, PLAYER_BODY.h);
    scene.physics.add.existing(this.zone, false);
    this.body = this.zone.body as Phaser.Physics.Arcade.Body;
    this.body.setCollideWorldBounds(true);
    this.body.setMaxVelocity(4000, this.t.maxFall);
    ctx.world.addCollider(this.zone, { canDrop: () => this.dropT > 0 });
    const me = this;
    const pilot: KitPilot = {
      get x() {
        return me.x;
      },
      get feetY() {
        return me.feetY;
      },
      get cy() {
        return me.feetY - PLAYER_BODY.h / 2;
      },
      get onGround() {
        return me.body.blocked.down || me.body.touching.down;
      },
      get speedX() {
        return me.body.velocity.x;
      },
      get isAlive() {
        return me.alive;
      },
      self: rival,
      viewRoot: rival.view.root,
      lunge: (dir, sec, mult) => me.lunge(dir, sec, mult),
      teleport: (x, fy) => {
        me.body.reset(x, fy - PLAYER_BODY.h / 2 - 2);
        me.facing = me.ctx.player.x >= x ? 1 : -1;
      },
      volley: (n) => me.volley(n),
    };
    this.kit = new BotKit(ctx, mechId, Math.max(0, Math.min(2, level)), pilot);
    this.push(0);
  }

  get x(): number {
    return this.body.center.x;
  }

  get feetY(): number {
    return this.body.bottom;
  }

  get isAlive(): boolean {
    return this.alive;
  }

  /** A player hit landed on the bot (already multiplied by the player's stats). */
  takeHit(h: Omit<HitMsg, 't'>): void {
    if (!this.alive || this.dashT > 0 || this.kit.phased > 0) return;
    let a = Math.max(0, Number(h.a) || 0) * this.kit.damageTaken;
    const fromShield = Math.min(this.shield, a);
    this.shield -= fromShield;
    a -= fromShield;
    this.hp = Math.max(0, this.hp - a);
    this.sinceHit = 0;
    // Knockback (lighter than on players; bots stand their ground a bit).
    if (!h.dot && h.kb && h.sx !== undefined) {
      const dir = Math.sign(this.x - h.sx) || 1;
      this.body.velocity.x += dir * Math.min(420, h.kb) * 0.6;
    }
    // Getting hit makes it want to dodge.
    if (this.dashCd <= 0 && Math.random() < this.lv.dash * 1.5) this.startDash(Math.random() < 0.5 ? 1 : -1);
    if (this.hp <= 0) this.die();
  }

  /** Server granted the bot a drop. */
  grant(kind: string, weapon: WeaponId | null): void {
    if (kind === 'health') this.hp = Math.min(this.maxHp, this.hp + this.maxHp * 0.4);
    else if (kind === 'weapon' && weapon) {
      this.weapon = weapon;
      this.burstLeft = 0;
    } else this.shield = this.maxShield;
  }

  private die(): void {
    this.alive = false;
    this.body.setVelocity(0, 0);
    if (!this.reportedDead) {
      this.reportedDead = true;
      this.report({ t: 'dead', round: this.round });
    }
  }

  update(dt: number, fighting: boolean, drops: BotDrop[], claim: (id: number) => void): void {
    const b = this.body;
    const p = this.ctx.player;
    const onGround = b.blocked.down || b.touching.down;
    if (onGround && !this.wasGround) this.airJumps = 1;
    if (!onGround && this.wasGround && b.velocity.y < 0) this.sinceTakeoff = 0;
    this.wasGround = onGround;
    this.sinceTakeoff += dt;
    this.sinceHit += dt;
    this.dropT = Math.max(0, this.dropT - dt);
    this.dashCd = Math.max(0, this.dashCd - dt);
    if (this.sinceHit > SHIELD_DELAY) this.shield = Math.min(this.maxShield, this.shield + SHIELD_REGEN * dt);

    if (!this.alive || !fighting) {
      b.setVelocityX(b.velocity.x * Math.exp(-dt * 10));
      b.setGravityY(this.t.gravity);
      this.push(dt);
      return;
    }

    this.kit.update(dt, fighting, this.lastDist, this.lastLos);
    this.rival.view.setAlpha(this.kit.phased > 0 ? 0.35 : 1);

    // ── Dash ──
    if (this.dashT > 0) {
      this.dashT -= dt;
      b.setGravityY(0);
      b.setVelocity(this.dashDir * this.t.dashSpeed * this.dashSpeedMult, 0);
      if (this.dashT <= 0) this.dashSpeedMult = 1;
      this.push(dt);
      return;
    }
    b.setGravityY(this.t.gravity * (b.velocity.y > 0 ? this.t.fallGravityMult : 1));

    const dx = p.x - this.x;
    const dy = p.y - (this.feetY - PLAYER_BODY.h / 2);
    const dist = Math.hypot(dx, dy);
    const def = WEAPONS[this.weapon];
    const range = Math.min(def?.range ?? 900, 950);
    const pAlive = p.alive;

    // ── Where to go ──
    let goalDir = 0;
    const hurt = this.hp < this.maxHp * 0.55;
    // Only drops within double-jump height, and not ones it already failed to reach.
    const want = drops
      .filter((d) => d.landed && !this.gaveUp.has(d.id) && Math.abs(d.x - this.x) < DROP_INTEREST && d.y > this.feetY - DROP_REACH && (d.kind !== 'health' || hurt))
      .sort((a, c) => Math.abs(a.x - this.x) - Math.abs(c.x - this.x))[0];
    if (want && want.id !== this.chaseId) {
      this.chaseId = want.id;
      this.chaseT = 0;
    }
    this.chaseT += dt;
    if (want && this.chaseT > DROP_PATIENCE) {
      this.gaveUp.add(want.id);
      this.chaseId = -1;
    } else if (want) {
      goalDir = Math.abs(want.x - this.x) > 18 ? Math.sign(want.x - this.x) : 0;
      if (Math.hypot(want.x - this.x, want.y - (this.feetY - PLAYER_BODY.h / 2)) < 60) claim(want.id);
      if (want.y < this.feetY - 150 && onGround && this.hopT <= 0) this.jump();
    } else if (pAlive && this.blindT > 0.8) {
      // Can't see the player: close in (and climb / drop) until there is a clear shot.
      goalDir = Math.abs(dx) > 40 ? Math.sign(dx) : 0;
    } else if (pAlive) {
      const lo = range * 0.35;
      const hi = range * 0.75;
      if (Math.abs(dx) > hi) goalDir = Math.sign(dx);
      else if (Math.abs(dx) < lo) goalDir = -Math.sign(dx) || 1;
      else {
        this.strafeT -= dt;
        this.plantCd -= dt;
        // Bastion plants and braces at range like a player would (shield bubble, -30% damage).
        if (this.mechId === 'bastion' && this.plantT <= 0 && this.plantCd <= 0 && onGround && this.blindT <= 0) {
          this.plantT = 1.8 + Math.random() * 1.6;
          this.plantCd = this.plantT + 1 + Math.random() * 1.5;
        }
        if (this.plantT > 0) {
          this.moveDir = 0;
          this.strafeT = 0.3;
        } else if (this.strafeT <= 0) {
          this.strafeT = 0.7 + Math.random() * 1.6;
          this.moveDir = Math.random() < 0.35 ? 0 : Math.random() < 0.5 ? 1 : -1;
        }
        goalDir = this.moveDir;
      }
    }
    if (this.plantT > 0) {
      this.plantT -= dt;
      // Pushed out of the fighting band, chasing a drop or lost sight: stop planting.
      if (goalDir !== 0 || this.blindT > 0.8) this.plantT = 0;
      else this.hopT = 0.5;
    }
    const planted = this.plantT > 0;
    const ax = (onGround ? this.t.groundAccel : this.t.airAccel) * dt;
    // Fortress anchors Bastion; Overload speeds Volt up.
    const targetVx = this.kit.anchored ? 0 : goalDir * this.t.runSpeed * this.kit.speedMult;
    const vx = b.velocity.x;
    b.setVelocityX(Math.abs(targetVx - vx) <= ax ? targetVx : vx + Math.sign(targetVx - vx) * ax);

    // ── Jumping / dropping ──
    this.hopT -= dt;
    const blocked = (goalDir > 0 && b.blocked.right) || (goalDir < 0 && b.blocked.left);
    if (onGround && !this.kit.anchored && (blocked || (pAlive && dy < -130 && this.hopT <= 0) || this.hopT < -2.5 || (this.blindT > 1.5 && this.hopT <= 0))) this.jump();
    else if (!onGround && this.airJumps > 0 && b.velocity.y > -60 && ((pAlive && dy < -90) || blocked)) {
      this.airJumps--;
      b.setVelocityY(-this.t.airJumpVelocity);
    }
    if (onGround && pAlive && dy > 150 && this.ctx.world.isOneWayAt(this.x, this.feetY + 4)) this.dropT = 0.3;

    // ── Dodge ──
    this.lastDist = dist;
    if (pAlive && !this.kit.anchored && this.dashCd <= 0 && dist < 520 && Math.random() < this.lv.dash * dt * (planted ? 0.6 : 3)) this.startDash(Math.random() < 0.5 ? 1 : -1);

    // ── Aim + fire ──
    if (pAlive) {
      const lead = Math.min(0.25, dist / Math.max(400, def?.projectileSpeed ?? 1500));
      const tx = p.x + p.vx * lead;
      const ty = p.y + p.vy * lead * 0.5;
      const muzzleY = this.feetY - 60;
      this.aim = Math.atan2(ty - muzzleY, tx - this.x);
      this.facing = tx >= this.x ? 1 : -1;
      const los = !this.ctx.world.raycast(this.x, muzzleY, p.x, p.y).hit;
      this.seeT = los ? this.seeT + dt : 0;
      this.blindT = los ? 0 : this.blindT + dt;
      this.lastLos = los;
      if (los && this.seeT >= this.lv.reaction && dist <= range * 1.05) this.fire(dt, dist);
      else this.firing = false;
    } else this.firing = false;
    this.push(dt);
  }

  /** Forced dash (Arc Dash lunge): ignores the dash cooldown. */
  lunge(dir: 1 | -1, sec: number, mult: number): void {
    this.facing = dir;
    this.dashDir = dir;
    this.dashT = sec;
    this.dashSpeedMult = mult;
    try {
      this.ctx.fx.afterimage(this.rival.view.root, { color: MECHS[this.mechId].reactorColor, alpha: 0.5 });
    } catch {
      // Cosmetic only.
    }
  }

  /** Fire the current weapon n times right now (Phase Echo volley). */
  volley(n: number): void {
    for (let i = 0; i < n; i++) {
      this.fireT = 0;
      this.firing = true;
      this.dutyT = 1;
      this.fire(0, 0);
    }
  }

  private jump(): void {
    this.body.setVelocityY(-this.t.jumpVelocity);
    this.hopT = 0.8 + Math.random() * 2.2;
    this.sinceTakeoff = 0;
  }

  private startDash(dir: 1 | -1): void {
    this.dashT = this.t.dashDuration;
    this.dashDir = dir;
    this.dashCd = this.t.dashCooldown * (1.4 + Math.random());
    try {
      this.ctx.fx.afterimage(this.rival.view.root, { color: MECHS[this.mechId].reactorColor, alpha: 0.4 });
      this.ctx.audio.play('dash', { volume: 0.5 });
    } catch {
      // Cosmetic only.
    }
  }

  // ───────────────────────────── Weapons ─────────────────────────────

  private fire(dt: number, dist: number): void {
    const def = WEAPONS[this.weapon];
    if (!def) return;
    // Burst duty cycle: fire for a while, then breathe (reads like heat management).
    this.dutyT -= dt;
    if (this.dutyT <= 0) {
      this.firing = !this.firing;
      this.dutyT = this.firing ? 0.8 + Math.random() * 0.9 * this.lv.duty * 2 : (0.35 + Math.random() * 0.6) * (1.2 - this.lv.duty);
    }
    if (!this.firing) return;
    this.fireT -= dt;
    if (this.fireT > 0) return;
    const m = this.rival.view.muzzleWorld();
    const ang = this.aim + (Math.random() - 0.5) * 2 * this.lv.aimError;
    const dmg = def.damage * this.lv.damage;
    const tags = def.tags as Tag[];
    switch (def.behavior) {
      case 'beam_chain': {
        this.fireT = 1 / Math.max(1, def.fireRate);
        const p = this.ctx.player;
        if (dist > def.range) return;
        try {
          this.ctx.fx.lightning([{ x: m.x, y: m.y }, { x: (m.x + p.x) / 2 + (Math.random() - 0.5) * 30, y: (m.y + p.y) / 2 + (Math.random() - 0.5) * 30 }, { x: p.x, y: p.y }], { color: def.muzzleColor, width: 3, duration: 90, jitter: 12 });
        } catch {
          // Cosmetic only.
        }
        if (Math.random() > this.lv.aimError * 2) this.ctx.damage.hitPlayer({ amount: dmg, tags, source: 'enemy', sourceX: m.x, sourceY: m.y, knockback: def.knockback, label: 'rival' });
        break;
      }
      case 'charge':
        this.fireT = (def.chargeTime ?? 0.7) + 0.5 + Math.random() * 0.4;
        this.shoot(def, m, ang, dmg, tags);
        break;
      case 'missile_burst':
        if (this.burstLeft <= 0) this.burstLeft = def.burst?.count ?? 4;
        this.burstLeft--;
        this.fireT = this.burstLeft > 0 ? (def.burst?.interval ?? 0.08) : 1 / Math.max(0.3, def.fireRate);
        this.shoot(def, m, ang - 0.25 + Math.random() * 0.5, dmg, tags);
        break;
      default: {
        this.fireT = 1 / Math.max(0.5, def.fireRate);
        const n = Math.max(1, def.pellets);
        for (let i = 0; i < n; i++) {
          const spread = n > 1 ? (i / (n - 1) - 0.5) * 2 * def.spread : (Math.random() - 0.5) * 2 * def.spread;
          // Lobbers aim a little high.
          this.shoot(def, m, ang + spread - (def.gravity ? 0.18 : 0), dmg, tags);
        }
      }
    }
    this.rival.recoil();
    try {
      this.ctx.audio.play(def.sfx as never, { volume: 0.45 });
    } catch {
      // Optional sound.
    }
  }

  private shoot(def: (typeof WEAPONS)[WeaponId], m: { x: number; y: number }, ang: number, dmg: number, tags: Tag[]): void {
    this.ctx.projectiles.spawn({
      owner: 'enemy',
      x: m.x,
      y: m.y,
      angle: ang,
      speed: def.projectileSpeed,
      damage: dmg,
      tags,
      texture: def.projectile.texture,
      scale: def.projectile.scale,
      tint: def.projectile.tint,
      glow: true,
      radius: def.projectile.radius,
      lifetime: Math.min(4, def.range / Math.max(200, def.projectileSpeed)),
      pierce: def.pierce,
      gravity: def.gravity,
      homing: def.homing,
      knockback: def.knockback,
      explode: def.explode ? { radius: def.explode.radius, damage: dmg * def.explode.damageMult } : undefined,
      field: def.field ? { radius: def.field.radius, duration: def.field.duration, strength: def.field.strength, implodeDamage: dmg * def.field.implodeDamageMult } : undefined,
      weaponId: def.id,
    });
  }

  // ───────────────────────────── Snapshot → rival view ─────────────────────────────

  private push(dt: number): void {
    const b = this.body;
    const onGround = b.blocked.down || b.touching.down;
    const anims = art.mechs[this.mechId]?.anims;
    let anim = 'idle';
    let fr = 0;
    let fc = anims?.idle.count ?? 8;
    this.animT += dt;
    if (!this.alive) {
      anim = 'death';
    } else if (this.dashT > 0) {
      anim = 'dash';
      fc = anims?.dash.count ?? 8;
      fr = Math.min(fc - 1, Math.floor((1 - this.dashT / Math.max(0.01, this.t.dashDuration)) * fc));
    } else if (!onGround) {
      anim = 'jump';
      fc = anims?.jump.count ?? 8;
      fr = Math.min(fc - 1, Math.floor(jumpPhase(b.velocity.y, this.sinceTakeoff, this.t) * fc));
    } else if (Math.abs(b.velocity.x) > 30) {
      anim = 'run';
      fc = anims?.run.count ?? 12;
      const k = Math.min(1.4, Math.max(0.55, Math.abs(b.velocity.x) / this.t.runSpeed));
      fr = Math.floor(this.animT * (anims?.run.fps ?? 18) * k) % fc;
    } else {
      fr = Math.floor(this.animT * (anims?.idle.fps ?? 8)) % fc;
    }
    const s: StateMsg = {
      t: 'state',
      x: this.x,
      y: this.feetY,
      vx: 0,
      vy: 0,
      f: this.facing,
      aim: this.aim,
      anim,
      fr,
      fc,
      spin: 0,
      tuck: 0,
      hp: Math.round(this.hp),
      maxHp: this.maxHp,
      sh: Math.round(this.shield),
      maxSh: this.maxShield,
      alive: this.alive,
      weapon: this.weapon,
      dash: this.dashT > 0,
    };
    this.rival.apply(s);
  }

  destroy(): void {
    this.kit.destroy();
    if (this.zone.active) this.zone.destroy();
  }
}

export const BOT_COLOR = COLORS.threat;
